// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import { styled } from '@compiled/react';
import find from 'lodash/find';
import { Field } from '@atlaskit/form';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src';
import { injectIntlV2 as injectIntl, type IntlShapeV2 as IntlShape } from '@atlassian/jira-intl';
import {
	COMPONENT_LEAD,
	PROJECT_DEFAULT,
	PROJECT_LEAD,
	UNASSIGNED,
} from '@atlassian/jira-project-directories-components/src/common/constants/default-assignee';
import type { DefaultAssignee as DefaultProjectAssignee } from '@atlassian/jira-project-directories-components/src/state/project/types';
import type { DefaultAssignee as DefaultComponentAssignee } from '../../model';
import messages from './messages';

type SelectItem = {
	value: DefaultComponentAssignee;
	label: string;
};

type State = {
	selectedItem?: SelectItem | undefined;
	isOpen?: boolean;
	value?: DefaultComponentAssignee | undefined;
	componentLeadName?: string | undefined;
};

type Props = {
	intl: IntlShape;
	// eslint doesn't detect use for some reason
	projectDefaultAssignee: DefaultProjectAssignee | undefined;
	// eslint doesn't detect use for some reason
	componentLeadName: string | undefined;
	onSelected: (arg1: DefaultComponentAssignee) => void;
	label: string;
	// eslint doesn't detect use for some reason
	projectLeadName: string | undefined;
	value: DefaultComponentAssignee | undefined;
};

const getAssigneeItems = (props: Props) => {
	const {
		intl: { formatMessage },
		componentLeadName,
		projectDefaultAssignee,
		projectLeadName,
	} = props;

	const unassignedMessage = formatMessage(messages.assigneeUnassigned);
	const projectLeadMessage = formatMessage(messages.assigneeProjectLead);
	const projectDefaultMessage = formatMessage(messages.assigneeProjectDefault);

	return [
		{
			value: COMPONENT_LEAD,
			label: formatMessage(messages.assigneeTypeWithDetails, {
				assigneeType: formatMessage(messages.assigneeComponentLead),
				displayName: componentLeadName || unassignedMessage,
			}),
		},
		{
			value: PROJECT_DEFAULT,
			label: projectDefaultAssignee
				? formatMessage(messages.assigneeTypeWithDetails, {
						assigneeType: projectDefaultMessage,
						displayName:
							projectDefaultAssignee === UNASSIGNED ? unassignedMessage : projectLeadMessage,
					})
				: projectDefaultMessage,
		},
		{
			value: PROJECT_LEAD,
			label: projectLeadName
				? formatMessage(messages.assigneeTypeWithDetails, {
						assigneeType: projectLeadMessage,
						displayName: projectLeadName,
					})
				: projectLeadMessage,
		},
		{
			value: UNASSIGNED,
			label: unassignedMessage,
		},
	] as const;
};

const getAssigneeItemByValue = (props: Props, value?: string): SelectItem | undefined => {
	const futureAssigneeItems = getAssigneeItems(props);
	return find(futureAssigneeItems, (i) => i.value === value);
};

// eslint-disable-next-line jira/react/no-class-components
export class AssigneeSelect extends PureComponent<Props, State> {
	static defaultProps = {
		label: '',
	};

	static getDerivedStateFromProps(nextProps: Props, prevState: State) {
		const selectedItem = prevState.selectedItem;
		const nextState: State = {
			value: nextProps.value,
			componentLeadName: nextProps.componentLeadName,
		};

		if (prevState.value !== nextProps.value) {
			if (!selectedItem || nextProps.value !== selectedItem.value) {
				nextState.selectedItem = getAssigneeItemByValue(nextProps, nextProps.value);
			}
		} else if (selectedItem && prevState.componentLeadName !== nextProps.componentLeadName) {
			const updatedCurrentSelection = getAssigneeItemByValue(nextProps, selectedItem.value);

			if (updatedCurrentSelection && updatedCurrentSelection.label !== selectedItem.label) {
				nextState.selectedItem = updatedCurrentSelection;
			}
		}
		return nextState;
	}

	constructor(props: Props) {
		super(props);

		this.state = {
			selectedItem: props.value ? getAssigneeItemByValue(props, props.value) : undefined,
			isOpen: false,
		};
	}

	state: State;

	onSelected = (selected: SelectItem) => {
		this.setState({
			selectedItem: selected,
			isOpen: false,
		});

		this.props.onSelected(selected.value);
	};

	onMenuOpenHandler = () => this.setState({ isOpen: true });

	onMenuCloseHandler = () => this.setState({ isOpen: false });

	render() {
		const {
			intl: { formatMessage },
			label,
		} = this.props;
		const { selectedItem, isOpen } = this.state;

		return (
			<Field name="component-assignee" label={<FieldLabelWrapper>{label}</FieldLabelWrapper>}>
				{({ fieldProps }) => (
					<Select
						inputId={fieldProps.id}
						noOptionsMessage={() => formatMessage(messages.noMatches)}
						menuPortalTarget={document.body}
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @typescript-eslint/no-explicit-any -- Ignored via go/DSP-18766
						styles={{ menuPortal: (base: any) => ({ ...base, zIndex: layers.modal }) }}
						placeholder={formatMessage(messages.placeholder)}
						options={getAssigneeItems(this.props)}
						// @ts-expect-error - TS2322 - Type '(selected: SelectItem) => void' is not assignable to type '(value: SelectItem | null, action: ActionMeta<SelectItem>) => void'.
						onChange={this.onSelected}
						value={selectedItem}
						menuIsOpen={isOpen}
						onMenuOpen={this.onMenuOpenHandler}
						onMenuClose={this.onMenuCloseHandler}
					/>
				)}
			</Field>
		);
	}
}

// @ts-expect-error - TS2345 - Argument of type 'typeof AssigneeSelect' is not assignable to parameter of type 'ComponentType<Record<any, any>>'.
export default injectIntl(AssigneeSelect);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldLabelWrapper = styled.div({
	display: 'inline-block',
	/* The AK Field component has a margin-top of 8px. However the label for the component lead UserPicker has padding-top */
	/* of 20px. We wrap our AK Field labels with an extra 12px of padding to keep spacing consistent. */
	marginTop: token('space.150', '12px'),
});
